/* eslint-disable */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Gavel, Clock, Loader2, Sparkles, ArrowRight, RefreshCw, Inbox, Radar } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API } from "@/lib/constants";

function plusDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function money(amount, ccy) {
  if (amount == null) return null;
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy || "NGN", maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${ccy || ""} ${Math.round(amount).toLocaleString()}`;
  }
}

const OUTCOME_LABEL = {
  kept: "Keep live, reschedule the chase",
  dead: "Close as dead, record the loss",
  back_to_board: "Ball is back with us, make a task",
  unclear: "Not sure. Use a button below",
};

// Capture is the PRIMARY path. The plain buttons are a deliberate fallback and are styled
// quietly so people reach for the sentence first — writing the reason is the whole point.
function WaitCard({ w, onChanged }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [signals, setSignals] = useState(null);
  const [chaseDate, setChaseDate] = useState(plusDaysISO(10));
  const [chooseOpp, setChooseOpp] = useState(null);   // null | {options:[...]} | {none:true}
  const [pending, setPending] = useState(null);       // remembers the outcome+extra while asking

  useEffect(() => {
    let live = true;
    fetch(`${API}/waits/${w.wait_id}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (live && d) setSignals(d.signals); });
    return () => { live = false; };
  }, [w.wait_id]);

  const propose = useCallback(async () => {
    if (!text.trim()) { toast.error("Tell Rex what happened first"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/waits/${w.wait_id}/triage/parse`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }),
      });
      const d = await r.json();
      setProposal(d);
      if (d.chase_date) setChaseDate(d.chase_date);
    } catch { toast.error("Could not read that"); }
    setBusy(false);
  }, [text, w.wait_id]);

  const apply = useCallback(async (outcome, extra = {}) => {
    setBusy(true);
    try {
      const body = { outcome, note: text, ...extra };
      const r = await fetch(`${API}/waits/${w.wait_id}/triage/apply`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (r.status === 409) {
        const det = d.detail || {};
        setPending({ outcome, extra });
        setChooseOpp(det.reason === "no_opportunity" ? { none: true } : { options: det.options || [] });
        setBusy(false); return;
      }
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Could not apply"); setBusy(false); return; }
      setChooseOpp(null); setPending(null);
      const msg = outcome === "dead" ? "Recorded the loss and closed the deal."
        : outcome === "back_to_board" ? "Put back on the board as a task."
        : "Kept live. Chase rescheduled.";
      toast.success(msg);
      onChanged();
    } catch { toast.error("Network error"); setBusy(false); }
  }, [text, w.wait_id, onChanged]);

  const confirmProposal = useCallback(() => {
    if (!proposal) return;
    const o = proposal.outcome;
    if (o === "kept") apply("kept", { chase_date: proposal.chase_date || chaseDate, follow_on: proposal.follow_on || null });
    else if (o === "dead") apply("dead", { loss: proposal.loss, follow_on: proposal.follow_on || null });
    else if (o === "back_to_board") apply("back_to_board", { follow_on: proposal.follow_on || null });
    else toast.error("Rex could not tell. Use a button below");
  }, [proposal, chaseDate, apply]);

  const val = money(w.value_amount, w.value_currency);

  return (
    <Card className="border-slate-200" data-testid={`wait-card-${w.wait_id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-semibold text-slate-900">{w.title || "Untitled wait"}</p>
            <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500">
              {w.client_name && <span className="text-blue-700">{w.client_name}</span>}
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{w.days_waiting}d waiting</span>
              {w.is_due && <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px]">chase due</Badge>}
              {w.decision_needed && <Badge className="bg-red-100 text-red-800 border border-red-200 text-[10px]">decision needed</Badge>}
              {val && <span className="text-slate-700 font-medium">{val}</span>}
            </div>
            {signals && (
              <p className="text-xs text-slate-400 mt-1 flex items-center gap-1" data-testid={`wait-signals-${w.wait_id}`}>
                <Radar className="w-3 h-3" />{signals.summary}
              </p>
            )}
          </div>
        </div>

        {/* PRIMARY: capture */}
        <div className="mt-4">
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">What happened?</label>
          <textarea
            data-testid={`wait-capture-${w.wait_id}`}
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Tell Rex in your own words — e.g. 'they pushed it to the new year' or 'they went with a competitor'."
            rows={3}
            className="mt-1 w-full rounded-lg border border-slate-300 focus:border-[#00A6DA] focus:ring-1 focus:ring-[#00A6DA] px-3 py-2 text-sm outline-none resize-none"
          />
          <div className="flex items-center gap-3 mt-2">
            <Button onClick={propose} disabled={busy} data-testid={`wait-propose-${w.wait_id}`}
              className="bg-[#174987] hover:bg-[#07152D] text-white">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Read &amp; propose
            </Button>
          </div>
        </div>

        {/* Proposal (confirmation gate) */}
        {proposal && (
          <div className="mt-3 rounded-lg bg-[#F3FAFD] border border-[#00A6DA]/30 p-3" data-testid={`wait-proposal-${w.wait_id}`}>
            <p className="text-sm text-slate-800">
              <span className="font-semibold">Rex proposes:</span> {OUTCOME_LABEL[proposal.outcome] || proposal.outcome}
            </p>
            {proposal.rationale && <p className="text-xs text-slate-500 mt-0.5">{proposal.rationale}</p>}
            {proposal.outcome === "kept" && (
              <div className="mt-2 text-sm flex items-center gap-2">
                <span className="text-slate-600">Chase on</span>
                <input type="date" value={proposal.chase_date || chaseDate}
                  onChange={e => setProposal({ ...proposal, chase_date: e.target.value })}
                  className="border border-slate-300 rounded px-2 py-1 text-sm" />
              </div>
            )}
            {proposal.outcome === "dead" && proposal.loss && (
              <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                <div><span className="font-medium">What happened:</span> {proposal.loss.what_happened}</div>
                <div><span className="font-medium">Whose account:</span> {proposal.loss.account}</div>
                {proposal.loss.competitive && <div>Competitive{proposal.loss.competitor ? `, against ${proposal.loss.competitor}` : ""}</div>}
              </div>
            )}
            {proposal.follow_on && proposal.follow_on.title && (
              <p className="text-xs text-slate-600 mt-2 flex items-center gap-1">
                <ArrowRight className="w-3 h-3" />Follow-on task: {proposal.follow_on.title}
              </p>
            )}
            {proposal.outcome !== "unclear" && (
              <Button onClick={confirmProposal} disabled={busy} data-testid={`wait-confirm-${w.wait_id}`}
                className="mt-3 bg-[#00A6DA] hover:bg-[#174987] text-white">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Confirm
              </Button>
            )}
          </div>
        )}

        {/* Ask which deal, or say there is none — never a silent success */}
        {chooseOpp && (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid={`wait-choose-opp-${w.wait_id}`}>
            {chooseOpp.none ? (
              <p className="text-sm text-amber-800">No open deal found for this client, so nothing was recorded. Link a deal to this wait first, then triage.</p>
            ) : (
              <>
                <p className="text-sm text-amber-900 font-medium">Which deal is this about?</p>
                <div className="mt-2 flex flex-col gap-1.5">
                  {chooseOpp.options.map(o => (
                    <button key={o.opportunity_id} data-testid={`wait-choose-${o.opportunity_id}`} disabled={busy}
                      onClick={() => { const p = pending; setChooseOpp(null); apply(p.outcome, { ...p.extra, opportunity_id: o.opportunity_id }); }}
                      className="text-left text-sm rounded border border-slate-300 bg-white px-3 py-1.5 hover:border-amber-400">
                      {o.opportunity_name} <span className="text-xs text-slate-400">· {o.stage}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* FALLBACK: quiet buttons, deliberately secondary */}
        <div className="mt-3 pt-2 border-t border-slate-100 flex items-center gap-4 text-xs text-slate-400">
          <span>or, quickly:</span>
          <button className="hover:text-slate-700 underline underline-offset-2" disabled={busy}
            data-testid={`wait-btn-live-${w.wait_id}`}
            onClick={() => apply("kept", { chase_date: chaseDate })}>still live</button>
          <button className="hover:text-red-600 underline underline-offset-2" disabled={busy}
            data-testid={`wait-btn-dead-${w.wait_id}`}
            onClick={() => apply("dead", { loss: { what_happened: "no_decision", account: "our_assessment", free_text: text || "Closed via quick button" } })}>dead</button>
          <button className="hover:text-blue-700 underline underline-offset-2" disabled={busy}
            data-testid={`wait-btn-mine-${w.wait_id}`}
            onClick={() => apply("back_to_board", {})}>actually mine</button>
        </div>
      </CardContent>
    </Card>
  );
}

function CollapsedWait({ w, onChanged }) {
  const [open, setOpen] = useState(false);
  if (open) return <WaitCard w={w} onChanged={onChanged} />;
  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      data-testid={`wait-collapsed-${w.wait_id}`}
      className="w-full flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-left hover:border-slate-300 hover:bg-slate-50 transition-colors"
    >
      <div className="min-w-0 flex items-center gap-2">
        {w.client_name && <span className="text-xs font-medium text-blue-700 shrink-0">{w.client_name}</span>}
        <span className="text-sm text-slate-800 truncate">{w.title || "Untitled wait"}</span>
      </div>
      <span className="text-xs text-slate-400 shrink-0 inline-flex items-center gap-1">
        <Clock className="w-3 h-3" />chase {w.chase_date || "not set"}
      </span>
    </button>
  );
}

function CandidateCard({ c, onConverted }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [chaseDate, setChaseDate] = useState(plusDaysISO(10));
  const [chooseOpp, setChooseOpp] = useState(null);   // null | {waitId, options} | {waitId, none:true}

  const propose = useCallback(async () => {
    if (!text.trim()) { toast.error("Tell Rex what happened first"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/waits/parse-text`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }),
      });
      const d = await r.json();
      setProposal(d);
      if (d.chase_date) setChaseDate(d.chase_date);
    } catch { toast.error("Could not read that"); }
    setBusy(false);
  }, [text]);

  // "with them": the ball is with the client — move the task off the board into a wait.
  const withThem = useCallback(async (cd) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/waits/from-task/${c.task_id}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: c.title, chase_date: cd || chaseDate, note: text }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "Could not move it"); setBusy(false); return; }
      toast.success("Moved off the board into a wait");
      onConverted();
    } catch { toast.error("Network error"); setBusy(false); }
  }, [c, chaseDate, text, onConverted]);

  // "dead": record the loss. The task becomes a wait, then the wait is closed as dead
  // (loss brief + pipeline + debrief draft) — the same path a wait takes when it dies.
  const applyDeadOnWait = useCallback(async (waitId, loss, opportunity_id) => {
    const r = await fetch(`${API}/waits/${waitId}/triage/apply`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ outcome: "dead", loss, note: text, opportunity_id }),
    });
    const d = await r.json().catch(() => ({}));
    if (r.status === 409) {
      const det = d.detail || {};
      setChooseOpp(det.reason === "no_opportunity" ? { waitId, none: true, loss } : { waitId, options: det.options || [], loss });
      setBusy(false); return;
    }
    if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Could not record the loss"); setBusy(false); return; }
    setChooseOpp(null);
    toast.success("Recorded the loss and closed the deal.");
    onConverted();
  }, [text, onConverted]);

  const dead = useCallback(async (loss) => {
    setBusy(true);
    try {
      const r1 = await fetch(`${API}/waits/from-task/${c.task_id}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: c.title, note: text }),
      });
      const d1 = await r1.json().catch(() => ({}));
      if (!r1.ok) { toast.error(d1.detail || "Could not record"); setBusy(false); return; }
      const waitId = d1.wait && d1.wait.wait_id;
      await applyDeadOnWait(waitId, loss, undefined);
    } catch { toast.error("Network error"); setBusy(false); }
  }, [c, text, applyDeadOnWait]);

  // "finished": it is already done — complete the task, nothing else.
  const finished = useCallback(async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/scrum/v2/tasks/${c.task_id}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Completed" }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d.detail || "Could not close it"); setBusy(false); return; }
      toast.success("Marked finished.");
      onConverted();
    } catch { toast.error("Network error"); setBusy(false); }
  }, [c, onConverted]);

  // "still mine": genuinely still my job — leave it on the board, record nothing.
  const stillMine = useCallback(() => { toast.success("Kept on your board."); onConverted(); }, [onConverted]);

  const confirmProposal = useCallback(() => {
    if (!proposal) return;
    const o = proposal.outcome;
    if (o === "kept") withThem(proposal.chase_date || chaseDate);          // still live with them → chase
    else if (o === "dead") dead(proposal.loss || { what_happened: "no_decision", account: "our_assessment", free_text: text });
    else if (o === "back_to_board") stillMine();                          // it's already my board task
    else toast.error("Rex could not tell. Use a button below");
  }, [proposal, chaseDate, text, withThem, dead, stillMine]);

  if (!open) {
    return (
      <div className="flex items-center justify-between gap-4 py-2 border-b border-slate-100" data-testid={`candidate-${c.task_id}`}>
        <div className="min-w-0">
          <p className="text-sm text-slate-800 truncate">{c.title}</p>
          <p className="text-xs text-slate-400">{c.client_name || "Internal"} · open {c.days_open}d · {c.status}</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}
          data-testid={`candidate-deal-${c.task_id}`} className="shrink-0">Deal with it</Button>
      </div>
    );
  }

  return (
    <div className="py-3 border-b border-slate-100" data-testid={`candidate-${c.task_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">{c.title}</p>
          <p className="text-xs text-slate-400">{c.client_name || "Internal"} · open {c.days_open}d · {c.status}</p>
        </div>
        <button className="text-xs text-slate-400 hover:text-slate-600 shrink-0" onClick={() => setOpen(false)}
          data-testid={`candidate-cancel-${c.task_id}`}>close</button>
      </div>

      {/* PRIMARY: capture — the same decision the waits have */}
      <div className="mt-3">
        <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">What happened?</label>
        <textarea
          data-testid={`candidate-capture-${c.task_id}`}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Tell Rex in your own words. e.g. 'still on me, I owe them a proposal', 'ball is with them now', or 'they went with someone else'."
          rows={3}
          className="mt-1 w-full rounded-lg border border-slate-300 focus:border-[#00A6DA] focus:ring-1 focus:ring-[#00A6DA] px-3 py-2 text-sm outline-none resize-none"
        />
        <Button onClick={propose} disabled={busy} data-testid={`candidate-propose-${c.task_id}`}
          className="mt-2 bg-[#174987] hover:bg-[#07152D] text-white">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
          Read &amp; propose
        </Button>
      </div>

      {proposal && (
        <div className="mt-3 rounded-lg bg-[#F3FAFD] border border-[#00A6DA]/30 p-3" data-testid={`candidate-proposal-${c.task_id}`}>
          <p className="text-sm text-slate-800">
            <span className="font-semibold">Rex proposes:</span>{" "}
            {proposal.outcome === "kept" ? "The ball is with them, set a chase date"
              : proposal.outcome === "dead" ? "Close as dead, record the loss"
              : proposal.outcome === "back_to_board" ? "Still yours, keep it on the board"
              : "Not sure — use a button below"}
          </p>
          {proposal.rationale && <p className="text-xs text-slate-500 mt-0.5">{proposal.rationale}</p>}
          {proposal.outcome === "kept" && (
            <div className="mt-2 text-sm flex items-center gap-2">
              <span className="text-slate-600">Chase on</span>
              <input type="date" value={proposal.chase_date || chaseDate}
                onChange={e => setProposal({ ...proposal, chase_date: e.target.value })}
                className="border border-slate-300 rounded px-2 py-1 text-sm" />
            </div>
          )}
          {proposal.outcome !== "unclear" && (
            <Button onClick={confirmProposal} disabled={busy} data-testid={`candidate-confirm-${c.task_id}`}
              className="mt-3 bg-[#00A6DA] hover:bg-[#174987] text-white">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Confirm
            </Button>
          )}
        </div>
      )}

      {/* Ask which deal, or say there is none — never a silent success */}
      {chooseOpp && (
        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid={`candidate-choose-opp-${c.task_id}`}>
          {chooseOpp.none ? (
            <p className="text-sm text-amber-800">No open deal found for this client, so nothing was recorded. Link a deal first, then triage.</p>
          ) : (
            <>
              <p className="text-sm text-amber-900 font-medium">Which deal is this about?</p>
              <div className="mt-2 flex flex-col gap-1.5">
                {chooseOpp.options.map(o => (
                  <button key={o.opportunity_id} data-testid={`candidate-choose-${o.opportunity_id}`} disabled={busy}
                    onClick={() => { setBusy(true); applyDeadOnWait(chooseOpp.waitId, chooseOpp.loss, o.opportunity_id); }}
                    className="text-left text-sm rounded border border-slate-300 bg-white px-3 py-1.5 hover:border-amber-400">
                    {o.opportunity_name} <span className="text-xs text-slate-400">· {o.stage}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* FALLBACK: quiet quick choices */}
      <div className="mt-3 pt-2 border-t border-slate-100 flex flex-wrap items-center gap-4 text-xs text-slate-400">
        <span>or, quickly:</span>
        <button className="hover:text-slate-700 underline underline-offset-2" disabled={busy}
          data-testid={`candidate-btn-mine-${c.task_id}`} onClick={stillMine}>still mine</button>
        <button className="hover:text-amber-700 underline underline-offset-2" disabled={busy}
          data-testid={`candidate-btn-withthem-${c.task_id}`}
          onClick={() => withThem(chaseDate)}>with them</button>
        <input type="date" value={chaseDate} onChange={e => setChaseDate(e.target.value)}
          data-testid={`candidate-chase-${c.task_id}`}
          className="border border-slate-200 rounded px-1.5 py-0.5 text-[11px] text-slate-600" />
        <button className="hover:text-red-600 underline underline-offset-2" disabled={busy}
          data-testid={`candidate-btn-dead-${c.task_id}`}
          onClick={() => dead({ what_happened: "no_decision", account: "our_assessment", free_text: text || "Closed via triage" })}>dead</button>
        <button className="hover:text-emerald-700 underline underline-offset-2" disabled={busy}
          data-testid={`candidate-btn-finished-${c.task_id}`} onClick={finished}>finished</button>
      </div>
    </div>
  );
}

export default function WaitsTriage() {
  const [waits, setWaits] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [counts, setCounts] = useState({ waiting: 0, chase_today: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wR, cR] = await Promise.all([
        fetch(`${API}/waits`, { credentials: "include", cache: "no-store" }),
        fetch(`${API}/waits/candidates`, { credentials: "include", cache: "no-store" }),
      ]);
      if (wR.ok) { const d = await wR.json(); setWaits(d.waits || []); setCounts(d.counts || {}); }
      if (cR.ok) { const d = await cR.json(); setCandidates(d.candidates || []); }
    } catch { toast.error("Could not load triage"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-4xl mx-auto py-6" data-testid="waits-triage-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Gavel className="w-6 h-6 text-[#174987]" />Triage</h1>
        <Button variant="ghost" size="sm" onClick={load} data-testid="triage-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>
      <p className="text-sm text-slate-500 mb-5" data-testid="triage-counts">
        Waiting on <strong>{counts.waiting || 0}</strong> · <strong>{counts.chase_today || 0}</strong> need chasing today
      </p>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          {waits.length === 0 ? (
            <Card className="border-dashed border-slate-200"><CardContent className="p-8 text-center text-sm text-slate-400">
              <Inbox className="w-6 h-6 mx-auto mb-2" />Nothing waiting right now.
            </CardContent></Card>
          ) : (
            <>
              {/* Due for chasing today — decision box open. Count matches the headline. */}
              <div className="space-y-3" data-testid="waits-due-list">
                {waits.filter(w => w.is_due).map(w => <WaitCard key={w.wait_id} w={w} onChanged={load} />)}
              </div>
              {/* Not due yet — collapsed one-liners below, each can be opened. */}
              {waits.some(w => !w.is_due) && (
                <div className="mt-8" data-testid="waits-later-section">
                  <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-1">Not due yet</h2>
                  <p className="text-xs text-slate-400 mb-3">Chasing later. Open one only if you want to act early.</p>
                  <div className="space-y-2">
                    {waits.filter(w => !w.is_due).map(w => <CollapsedWait key={w.wait_id} w={w} onChanged={load} />)}
                  </div>
                </div>
              )}
            </>
          )}

          {candidates.length > 0 && (
            <div className="mt-8">
              <h2 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-1">To triage from the board</h2>
              <p className="text-xs text-slate-400 mb-3">Aged, still-open tasks. Deal with each one: say what happened, or pick a quick choice. Nothing is assumed from the title, and nothing is recorded until you confirm.</p>
              <Card className="border-slate-200"><CardContent className="p-4">
                {candidates.map(c => <CandidateCard key={c.task_id} c={c} onConverted={load} />)}
              </CardContent></Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
